import { Callback } from '../../../types';
import { DEFAULT_VIDEO_CONSTRAINTS } from '../../../constants';
import { isMobile } from '../../../utils';
import Video, { ConnectOptions, LocalTrack, Room } from 'twilio-video';
import { VideoRoomMonitor } from '@twilio/video-room-monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMessageEventEmitter } from '../../../hooks/useMessage/useMessageEventEmitter';

// @ts-ignore
window.TwilioVideo = Video;

interface Participant {
  identity: string;
  sid: string;
}

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const optionsRef = useRef(options);
  const event = useMessageEventEmitter();

  const mapperParticipant = ({ identity, sid }: Participant) => ({ identity, sid });

  const handleChangedParticipants = (currentRoom: Room) => {
    const localParticipant = mapperParticipant(currentRoom.localParticipant);
    const otherParticipants = Array.from(currentRoom.participants).map(([_, participant]): any =>
      mapperParticipant(participant)
    );
    const participants = [localParticipant, ...otherParticipants].sort((a, b) => a.identity.localeCompare(b.identity));

    event.emit('CHANGE_PARTICIPANTS', participants);
  };

  useEffect(() => {
    // This allows the connect function to always access the most recent version of the options object. This allows us to
    // reliably use the connect function at any time.
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(
    token => {
      setIsConnecting(true);
      return Video.connect(token, {
        ...optionsRef.current,
        tracks: localTracks,
        video: DEFAULT_VIDEO_CONSTRAINTS,
      }).then(
        newRoom => {
          setRoom(newRoom);
          VideoRoomMonitor.registerVideoRoom(newRoom);
          const disconnect = () => newRoom.disconnect();

          // This app can add up to 16 'participantDisconnected' listeners to the room object, which can trigger
          // a warning from the EventEmitter object. Here we increase the max listeners to suppress the warning.
          newRoom.setMaxListeners(16);

          newRoom.once('disconnected', () => {
            // Reset the room only after all other `disconnected` listeners have been called.
            setTimeout(() => setRoom(null));
            window.removeEventListener('beforeunload', disconnect);

            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;

          newRoom.localParticipant.videoTracks.forEach(publication =>
            // All video tracks are published with 'low' priority because the video track
            // that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            publication.setPriority('low')
          );

          setIsConnecting(false);

          // Add a listener to disconnect from the room when a user closes their browser
          window.addEventListener('beforeunload', disconnect);

          if (isMobile) {
            // Add a listener to disconnect from the room when a mobile user closes their browser
            window.addEventListener('pagehide', disconnect);
          }

          // Event emitter for message events to parent iframes
          handleChangedParticipants(newRoom);
          newRoom.on('participantConnected', () => handleChangedParticipants(newRoom));
          newRoom.on('participantDisconnected', () => handleChangedParticipants(newRoom));
        },
        error => {
          localStorage.removeItem('data_last_connection');
          onError(error);
          setIsConnecting(false);
        }
      );
    },
    [handleChangedParticipants, localTracks, onError]
  );

  return { room, isConnecting, connect };
}
